import { getFieldFormNameWithIndex } from 'app/pages/loanApplication/LoanApplication.utils';
import _ from 'lodash';

import { loanFeeCicFormFields } from './LoanFeeCicForm.config';

const regExpToObject = /[a-zA-Z]+|[0-9]+/g;

export const transformLoanFeesCicForm = form =>
  Object.entries(form).reduce((acc, [formKey, formValue]) => {
    _.set(acc, formKey.match(regExpToObject), formValue);

    return acc;
  }, {});

export const mergeLoanFeesCic = ({
  isInitialMerge,
  cicFeeReasons,
  loanCicFees = {},
  loanFees,
  allFees,
}) => {
  if (_.isEmpty(cicFeeReasons)) {
    return [];
  }

  const resultedObject = cicFeeReasons.reduce((acc, { items }) => {
    items.forEach(item => {
      if (!item) {
        return;
      }

      const { feeTypeId, isRequired } = item;
      const feeInAcc = acc[feeTypeId];
      const isRewriteOptionalFee = isRequired && feeInAcc && !feeInAcc.isRequired;

      if (!feeInAcc || isRewriteOptionalFee) {
        const feeDataSource = [loanCicFees, loanFees, allFees].find(source => source?.[feeTypeId]); // Order is important.
        const feeData = isInitialMerge ? item : feeDataSource[feeTypeId];

        acc[feeTypeId] = {
          feeTypeId,
          isRequired,
          isDeleted: !!feeData?.isDeleted,
          amount: feeData?.borrowerPaidBeforeClosingAmount ?? feeData?.amount,
          name: feeDataSource?.[feeTypeId]?.name,
        };
      }
    });

    return acc;
  }, {});

  return Object.values(resultedObject);
};

export const getLoanFeeReasonsOutputData = ({ formValues, initialFormValues }) => {
  const [initialValues, values] = [initialFormValues, formValues].map(form =>
    transformLoanFeesCicForm(form),
  );

  const [initialLoanCicFees, loanCicFees] = [initialValues, values].map(data =>
    _.keyBy(data.loanCicFees, ({ feeTypeId }) => feeTypeId),
  );

  return values.cicFeeReasons.map(({ name, value: cicFeeReasonId, items }) => ({
    cicFeeReasonId,
    name,
    fees: items.map(({ feeTypeId, isRequired }) => {
      const { amount, isDeleted } = loanCicFees[feeTypeId];
      const initialAmount = initialLoanCicFees[feeTypeId]?.amount;

      const [initialAmountToCompare, amountToCompare] = [initialAmount, amount].map(value =>
        value?.toString() ? Number(value) : null,
      );

      const isAmountChanged = initialAmountToCompare !== amountToCompare;

      return {
        feeTypeId,
        amount: amount || 0,
        cicFeeReasonId,
        isRequired,
        isDeleted: isDeleted && isAmountChanged ? false : isDeleted,
      };
    }),
  }));
};

export const getAmountNotChangedErrors = ({ form, loanFees, pendingChangesData }) => {
  const { loanCicFees = [] } = transformLoanFeesCicForm(form);

  return loanCicFees.reduce((acc, { isRequired, amount, feeTypeId }, index) => {
    const loanFeeAmountPendingChanges = pendingChangesData[index]?.amount;

    const defaultAmount = loanFeeAmountPendingChanges
      ? loanFeeAmountPendingChanges.initialValue
      : loanFees[feeTypeId]?.amount;

    const isEmptyAmount = _.isEmpty(amount?.toString());
    const isEmptyDefaultAmount = _.isEmpty(defaultAmount?.toString());

    const amountValue = isEmptyAmount ? null : Number(amount);
    const defaultAmountValue = isEmptyDefaultAmount ? null : Number(defaultAmount);

    const isAmountChanged = defaultAmountValue !== amountValue;
    const isLoanFeeCustomDisabled = !!loanFees[feeTypeId]?.isCustomDisabled;

    const isAmountErrorOccurred = isRequired && !isAmountChanged && !isLoanFeeCustomDisabled;

    if (isAmountErrorOccurred) {
      const amountFieldNameInForm = getFieldFormNameWithIndex(index, loanFeeCicFormFields.amount);

      acc[amountFieldNameInForm] = 'Required amount should be changed';
    }

    return acc;
  }, {});
};

export const getCicFeeReasonsInvalidError = cicFeeReasonIdsToReselect => {
  const isCicFeeReasonRequiredToReselect =
    !!Object.values(cicFeeReasonIdsToReselect).filter(Boolean).length;

  if (isCicFeeReasonRequiredToReselect) {
    return { cicFeeReasons: 'Please re-select CIC Fee Reason(s)' };
  }
};

export const getOverriddenFeeIdPostfix = loanId => `${loanId}True`;
